<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { type ReviewImage, reviewImagesApi } from '@/api/entities/admin/reviewImages'

definePage({
  meta: { title: 'Review Images', action: 'read', subject: 'review' },
})

const { handleError } = useErrorHandler()
const loading = ref(false)
const items = ref<ReviewImage[]>([])
const currentPage = ref(1)
const lastPage = ref(1)

const headers = [
  { title: 'URL', key: 'url', sortable: false },
  { title: 'Content Length', key: 'content_length', sortable: false },
  { title: 'Content Type', key: 'content_type', sortable: false },
  { title: 'Driver Config ID', key: 'driver_config_id', sortable: false },
  { title: '생성일', key: 'created_at', sortable: false },
]

const loadItems = async () => {
  loading.value = true
  try {
    const { success, data } = await reviewImagesApi.list({ page: currentPage.value })
    if (success && data) {
      items.value = data.data
      lastPage.value = data.last_page
    }
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
  finally {
    loading.value = false
  }
}

const onPageChange = (page: number) => {
  currentPage.value = page
  loadItems()
}

const formatDate = (d: string | null) => d ? new Date(d).toLocaleString('ko-KR') : '-'

const formatBytes = (bytes: number | null) => {
  if (bytes === null) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const truncate = (s: string, n = 60) => s.length > n ? s.slice(0, n) + '...' : s

onMounted(loadItems)
</script>

<template>
  <VCard>
    <VCardTitle class="pa-4">
      Review Images
    </VCardTitle>
    <VCardText>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        hide-default-footer
      >
        <template #item.url="{ item }">
          <a
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-decoration-none"
          >
            {{ truncate(item.url) }}
          </a>
        </template>
        <template #item.content_length="{ item }">
          {{ formatBytes(item.content_length) }}
        </template>
        <template #item.content_type="{ item }">
          {{ item.content_type ?? '-' }}
        </template>
        <template #item.driver_config_id="{ item }">
          {{ item.driver_config_id ?? '-' }}
        </template>
        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #no-data>
          <div class="text-center py-8">
            데이터가 없습니다.
          </div>
        </template>
      </VDataTable>
      <div
        v-if="lastPage > 1"
        class="d-flex justify-center mt-4"
      >
        <VPagination
          :model-value="currentPage"
          :length="lastPage"
          @update:model-value="onPageChange"
        />
      </div>
    </VCardText>
  </VCard>
</template>
